/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/callbackProfilesApi.ts (Callback Profiles)
 🗓️ Created: 2025-01-15
 🧩 Description:
    API service functions for Callback Profiles.

────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  CallbackProfileResponse,
} from '../types';
import { ListCallbackProfilesParams } from '../types/callbackProfiles.types';

export interface ListCallbackProfilesResponse {
  success: boolean;
  data: CallbackProfileResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GetCallbackProfileResponse {
  success: boolean;
  data: CallbackProfileResponse;
}

export interface CreateCallbackProfilePayload {
  name: string;
  enabled?: boolean;
  settings: Record<string, unknown>;
}

export interface UpdateCallbackProfilePayload {
  name?: string;
  enabled?: boolean;
  settings?: Record<string, unknown>;
}

/**
 * Fetch list of callback profiles
 */
export async function fetchCallbackProfiles(
  params: ListCallbackProfilesParams
): Promise<ListCallbackProfilesResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());
  if (params.sortBy) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
  if (params.search) queryParams.set('search', params.search);
  if (params.domainId != null) queryParams.set('domainId', params.domainId.toString());

  const response = await fetch(buildApiUrl(`/api/telephony/callback-profiles?${queryParams}`), {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch callback profiles');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch callback profiles');
  }

  return result;
}

/**
 * Fetch single callback profile by ID
 */
export async function fetchCallbackProfileById(id: string | number): Promise<CallbackProfileResponse> {
  const url = buildApiUrl(`/api/telephony/callback-profiles/${id}`);

  const response = await fetch(url, {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch callback profile');
  }

  const result = await response.json();

  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Failed to fetch callback profile');
  }

  return result.data;
}

/**
 * Create callback profile
 */
export async function createCallbackProfile(
  payload: CreateCallbackProfilePayload
): Promise<CallbackProfileResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/callback-profiles'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error('Failed to create callback profile');
  }

  const result = await response.json();
  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Failed to create callback profile');
  }

  return result.data;
}

/**
 * Update callback profile
 */
export async function updateCallbackProfile(
  id: string | number,
  payload: UpdateCallbackProfilePayload
): Promise<CallbackProfileResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/callback-profiles/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error('Failed to update callback profile');
  }

  const result = await response.json();
  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Failed to update callback profile');
  }

  return result.data;
}

/**
 * Delete callback profile
 */
export async function deleteCallbackProfile(id: string | number): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/callback-profiles/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    let message = 'Failed to delete callback profile';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

/**
 * Fetch quick list of callback profiles (for QuickSearchModal)
 */
export async function fetchQuickListCallbackProfiles(search?: string): Promise<Array<{ id: number; name: string }>> {
  const params = new URLSearchParams({
    page: '1',
    limit: search && search.trim().length >= 2 ? '50' : '100',
    sortBy: 'name',
    sortOrder: 'asc',
    ...(search && search.trim().length >= 2 ? { search: search.trim() } : {})
  });

  const response = await fetch(buildApiUrl(`/api/telephony/callback-profiles?${params}`), {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (!result?.success || !Array.isArray(result?.data)) {
    return [];
  }

  return result.data.map((item: CallbackProfileResponse) => ({
    id: item.id,
    name: item.name
  }));
}

